import { fmtPrice } from "../lib/format";
import { Rating, VerifiedBadge } from "../components/Rating";
import { Testimonials, StatsBar, VettedPros, FAQ, Partners } from "../components/home/TrustSections";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n, catName, tr } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Search, MapPin, ArrowRight, ShieldCheck, CreditCard, MessageSquare } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import USMap, { ACTIVE_CITIES } from "../components/USMap";
import CategoryIcon from "../components/CategoryIcon";
import { useSEO } from "../lib/useSEO";
import { SITE_URL, DEFAULT_TITLE, DEFAULT_DESCRIPTION, OG_IMAGE, orgJsonLd, websiteJsonLd } from "../lib/seo";

const HERO_IMG = "https://customer-assets-wrfwihn1.emergentagent.net/job_service-marketplace-447/artifacts/9o980ajd_shawn-henley-3CQQlwWpn70-unsplash.webp";

function UseHomeSEO() {
  useSEO({
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    canonical: `${SITE_URL}/`,
    ogImage: OG_IMAGE,
    jsonLd: [orgJsonLd(), websiteJsonLd()],
  });
  return null;
}
const FL = {
  brickell: "https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?auto=format&fit=crop&w=1800&q=75", // Miami luxury poolside at dusk
  southBeach: "https://images.unsplash.com/photo-1580655653885-65763b2597d0?auto=format&fit=crop&w=1800&q=75", // South Beach lifeguard tower
  wynwood: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1800&q=75", // creative workspace collaboration
  palm: "https://images.unsplash.com/photo-1506966953602-c20cc11f75e3?auto=format&fit=crop&w=1800&q=75", // Miami palms & pastel
  oceanDrive: "https://images.unsplash.com/photo-1512909006721-3d6018887383?auto=format&fit=crop&w=1800&q=75", // Ocean Drive Art Deco
  keys: "https://images.unsplash.com/photo-1509233725247-49e657c54213?auto=format&fit=crop&w=1800&q=75", // Florida Keys turquoise
};

function ServiceCard({ s }) {
  const { lang } = useI18n();
  return (
    <Link to={`/services/${s.id}`} data-testid={`service-card-${s.id}`} className="service-card block bg-white border border-stone-200/70 rounded-2xl overflow-hidden">
      <div className="aspect-[4/3] bg-stone-100 overflow-hidden">
        <img src={s.images?.[0]} alt={tr(s, "title", lang)} className="w-full h-full object-cover" />
      </div>
      <div className="p-5">
        <div className="text-xs eyebrow text-stone-500">{catName(s.category, lang)}</div>
        <div className="font-serif text-xl mt-1 leading-tight line-clamp-2">{tr(s, "title", lang)}</div>
        <div className="flex items-center gap-2 mt-3 text-sm text-stone-600">
          {s.provider?.avatar && <img src={s.provider.avatar} className="w-6 h-6 rounded-full object-cover" alt={s.provider.name} />}
          <span>{s.provider?.name}</span>
          <VerifiedBadge compact />
          <span className="text-stone-300">·</span>
          <Rating rating={s.rating} count={s.review_count} />
        </div>
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-1 text-xs text-stone-500"><MapPin size={12} />{s.location}</div>
          <div className="font-serif text-lg">{fmtPrice(s.price)}</div>
        </div>
      </div>
    </Link>
  );
}

function ProviderCard({ p }) {
  const { lang } = useI18n();
  return (
    <Link to={`/providers/${p.id}`} className="service-card block bg-white border border-stone-200/70 rounded-2xl p-5" data-testid={`provider-card-${p.id}`}>
      <div className="flex items-center gap-4">
        <img src={p.avatar} className="w-16 h-16 rounded-full object-cover" alt={p.name} />
        <div>
          <div className="font-serif text-lg leading-tight flex items-center gap-2">{p.name} <VerifiedBadge compact /></div>
          <div className="text-xs text-stone-500 flex items-center gap-1"><MapPin size={12} />{p.location}</div>
          <div className="flex items-center gap-1 mt-1 text-sm"><Rating rating={p.rating} count={p.review_count} /></div>
        </div>
      </div>
      <p className="text-sm text-stone-600 mt-3 line-clamp-2">{tr(p, "bio", lang)}</p>
    </Link>
  );
}

function pickBalanced(list, n) {
  const byCat = {};
  list.forEach((s) => { (byCat[s.category_id] = byCat[s.category_id] || []).push(s); });
  const groups = Object.values(byCat);
  const out = [];
  for (let i = 0; out.length < n && groups.some((g) => g.length); i++) {
    groups.forEach((g) => { if (g.length && out.length < n) out.push(g.shift()); });
  }
  return out;
}

export default function HomePage() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState("");
  const [heroCat, setHeroCat] = useState("");
  const [cats, setCats] = useState([]);
  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/services?sort=recent").then((r) => setServices(pickBalanced(r.data, 6)));
    api.get("/providers").then((r) => setProviders(r.data));
  }, []);

  const POPULAR = lang === "fr"
    ? ["Chef privé", "Cours de français", "Photographe mariage", "Immobilier Miami", "Coiffure & maquillage"]
    : ["Private chef", "French lessons", "Wedding photographer", "Miami real estate", "Hair & makeup"];

  const submit = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (loc) p.set("location", loc);
    if (heroCat) p.set("category_id", heroCat);
    nav(`/services?${p.toString()}`);
  };
  const quick = (term) => nav(`/services?q=${encodeURIComponent(term)}`);

  return (
    <div>
      <UseHomeSEO />
      {/* HERO — full bleed image with centered headline */}
      <section className="relative -mt-20 h-[92vh] min-h-[640px] w-full overflow-hidden">
        <img src={HERO_IMG} alt="Aerial view of Miami Beach coastline at sunrise" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/15 to-black/45" />

        <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 pt-16 text-center">
          <h1 data-testid="hero-title" className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.02] tracking-tighter max-w-5xl drop-shadow-[0_4px_24px_rgba(0,0,0,0.35)]">
            {t("hero_title")}
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-white/95 font-light tracking-wide max-w-2xl drop-shadow-[0_2px_12px_rgba(0,0,0,0.35)]">
            {t("hero_subtitle")}
          </p>

          <form onSubmit={submit} className="mt-10 w-full max-w-4xl bg-white rounded-2xl p-2 flex flex-col sm:flex-row gap-2 shadow-[0_20px_60px_rgba(0,0,0,0.25)]">
            <div className="flex-1 flex items-center gap-2 px-3">
              <Search size={18} className="text-stone-400 shrink-0" />
              <Input data-testid="hero-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("hero_search_placeholder")} className="border-0 shadow-none focus-visible:ring-0 h-12 text-base" />
            </div>
            <div className="sm:border-l sm:border-stone-200 sm:pl-2">
              <Select value={loc} onValueChange={setLoc}>
                <SelectTrigger data-testid="hero-location-select" className="border-0 shadow-none h-12 w-full sm:w-44 text-base">
                  <MapPin size={16} className="text-stone-400 mr-1 shrink-0" /><SelectValue placeholder={t("hero_location_all")} />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {ACTIVE_CITIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:border-l sm:border-stone-200 sm:pl-2">
              <Select value={heroCat} onValueChange={setHeroCat}>
                <SelectTrigger data-testid="hero-category-select" className="border-0 shadow-none h-12 w-full sm:w-48 text-base">
                  <SelectValue placeholder={t("hero_category_placeholder")} />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {cats.map((c) => <SelectItem key={c.id} value={c.id}>{catName(c, lang)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="hero-search-btn" type="submit" className="rounded-xl h-12 bg-stone-900 hover:bg-stone-800 text-stone-50 px-8 btn-pill text-base">{t("search_btn")}</Button>
          </form>

          <div className="mt-5 flex flex-wrap items-center justify-center gap-2 text-xs" data-testid="popular-searches">
            <span className="text-white/70 uppercase tracking-wider text-[11px] mr-1">{t("popular_searches")}:</span>
            {POPULAR.map((term) => (
              <button key={term} type="button" onClick={() => quick(term)} data-testid={`popular-${term.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white backdrop-blur-sm transition-colors">
                {term}
              </button>
            ))}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-center gap-3">
            <Link to="/services"><Button data-testid="hero-book-btn" className="rounded-full h-12 px-8 bg-white text-black hover:bg-stone-200 btn-pill">{t("book_service_top")} <ArrowRight size={16} className="ml-2" /></Button></Link>
            <Link to="/register?role=provider"><Button data-testid="hero-provider-btn" variant="outline" className="rounded-full h-12 px-8 border-white/60 text-white bg-transparent hover:bg-white/10 hover:text-white btn-pill">{t("become_provider")}</Button></Link>
          </div>
        </div>
      </section>

      <StatsBar />

      {/* Popular categories — soft palm backdrop */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={FL.palm} alt="Palm trees and pastel Art Deco facades in Miami" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-white via-white/85 to-white" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="font-serif text-4xl sm:text-5xl mt-2">{t("popular_cats")}</h2>
            </div>
            <Link to="/categories" className="text-sm text-stone-600 hover:underline">{t("view_all")} <ArrowRight size={14} className="inline" /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {cats.slice(0, 8).map((c) => (
              <Link key={c.id} to={`/categories/${c.slug}`} data-testid={`category-${c.slug}`} className="service-card group relative rounded-2xl overflow-hidden aspect-[4/5] block shadow-[0_10px_40px_rgba(0,0,0,0.06)]">
                <img src={c.image} alt={catName(c, lang)} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />
                <div className="absolute top-4 left-4 h-11 w-11 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-stone-900 shadow-md ring-1 ring-white/60">
                  <CategoryIcon name={c.icon} size={20} />
                </div>
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <div className="font-serif text-xl">{catName(c, lang)}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Florida banner — Ocean Drive */}
      <section className="relative h-[38vh] min-h-[280px] w-full overflow-hidden">
        <img src={FL.oceanDrive} alt="Ocean Drive, Miami Beach" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-black/60" />
        <div className="relative z-10 h-full flex items-center justify-center text-center px-6">
          <div className="text-white max-w-3xl">
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight drop-shadow-[0_2px_16px_rgba(0,0,0,0.4)]">
              {t("banner_florida")}
            </h2>
          </div>
        </div>
      </section>

      {/* Featured services */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-serif text-4xl sm:text-5xl mt-2">{t("featured_services")}</h2>
          </div>
          <Link to="/services" className="text-sm text-stone-600 hover:underline">{t("view_all")} <ArrowRight size={14} className="inline" /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => <ServiceCard key={s.id} s={s} />)}
        </div>
      </section>

      {/* How it works — full bleed Wynwood */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={FL.wynwood} alt="Creative professionals collaborating in a Wynwood studio" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/85" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <div className="eyebrow text-white/60">— {t("how_title")}</div>
            <h2 className="font-serif text-5xl sm:text-6xl mt-3 text-white leading-tight">{t("how_subtitle")}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mt-14">
            {[
              [1, t("step_discover"), t("step_discover_d")],
              [2, t("step_book"), t("step_book_d")],
              [3, t("step_enjoy"), t("step_enjoy_d")],
            ].map(([n, title, d]) => (
              <div key={n} className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 text-white">
                <div className="font-serif text-5xl text-white/40">0{n}</div>
                <div className="font-serif text-2xl mt-4">{title}</div>
                <p className="text-white/80 mt-2 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured providers — soft beach backdrop */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={FL.keys} alt="Turquoise waters of the Florida Keys" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-white/90 to-white" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="font-serif text-4xl sm:text-5xl mt-2">{t("featured_providers")}</h2>
            </div>
            <Link to="/providers" className="text-sm text-stone-600 hover:underline">{t("view_all")} <ArrowRight size={14} className="inline" /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {providers.slice(0, 4).map((p) => <ProviderCard key={p.id} p={p} />)}
          </div>
        </div>
      </section>

      <Testimonials />

      <VettedPros providers={providers} />

      {/* US Cities Map */}
      <USMap />

      <Partners />

      <FAQ />

      {/* Trust — subtle South Beach tint */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={FL.southBeach} alt="South Beach lifeguard tower, Miami" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-50 via-stone-50/90 to-white" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h2 className="font-serif text-4xl sm:text-5xl mb-10">{t("trust_title")}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              [ShieldCheck, t("trust_1_t"), t("trust_1_d")],
              [CreditCard, t("trust_2_t"), t("trust_2_d")],
              [MessageSquare, t("trust_3_t"), t("trust_3_d")],
            ].map(([Icon, title, d], i) => (
              <div key={i} className="bg-white/70 backdrop-blur-sm border-t-2 border-stone-900 rounded-b-2xl pt-6 p-6">
                <Icon size={28} className="text-stone-700" />
                <div className="font-serif text-2xl mt-4">{title}</div>
                <p className="text-stone-600 mt-2 text-sm">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dual CTA — photo-backed cards */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="relative rounded-3xl overflow-hidden min-h-[320px] p-10 flex flex-col justify-end">
            <img src={FL.brickell} alt="Miami luxury lifestyle" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/20" />
            <div className="relative text-stone-50">
              <div className="eyebrow text-stone-300">{t("clients_label")} · Miami</div>
              <h3 className="font-serif text-4xl sm:text-5xl mt-3">{t("dual_customer_h")}</h3>
              <p className="mt-3 text-stone-200 text-sm max-w-md">{t("dual_customer_d")}</p>
              <Link to="/services"><Button data-testid="cta-customer" className="mt-6 rounded-full bg-stone-50 text-stone-900 hover:bg-white btn-pill">{t("cta_customer")}</Button></Link>
            </div>
          </div>
          <div className="relative rounded-3xl overflow-hidden min-h-[320px] p-10 flex flex-col justify-end">
            <img src={FL.wynwood} alt="Independent professionals collaborating" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/20" />
            <div className="relative text-stone-50">
              <div className="eyebrow text-stone-300">{t("providers_label")} · Community</div>
              <h3 className="font-serif text-4xl sm:text-5xl mt-3">{t("dual_provider_h")}</h3>
              <p className="mt-3 text-stone-200 text-sm max-w-md">{t("dual_provider_d")}</p>
              <Link to="/register?role=provider"><Button data-testid="cta-provider" className="mt-6 rounded-full bg-stone-50 text-stone-900 hover:bg-white btn-pill">{t("cta_provider")}</Button></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
